import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  getStepsForTour,
  TOUR_IDS,
  TOUR_VERSION,
} from '../config/portalTourSteps';

const PortalTourContext = createContext(null);

function storageKey(userId, tourId) {
  return `osabea_portal_tour_${TOUR_VERSION}_${tourId}_${userId || 'anon'}`;
}

function skippedKey(userId, tourId) {
  return `${storageKey(userId, tourId)}_skipped`;
}

export function PortalTourProvider({ children }) {
  const { user, isAuditor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userIsAuditor = isAuditor();

  const [activeTourId, setActiveTourId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetReady, setTargetReady] = useState(false);

  const userId = user?.user_id || user?.id || user?.email;

  const steps = useMemo(() => {
    if (!activeTourId) return [];
    return getStepsForTour(activeTourId, { isAuditor: userIsAuditor });
  }, [activeTourId, userIsAuditor]);

  const step = steps[stepIndex] || null;
  const isActive = Boolean(activeTourId && step);

  const markComplete = useCallback(
    (tourId) => {
      if (!tourId || !userId) return;
      try {
        localStorage.setItem(storageKey(userId, tourId), 'completed');
        localStorage.removeItem(skippedKey(userId, tourId));
      } catch {
        // ignore quota errors
      }
    },
    [userId],
  );

  const markSkipped = useCallback(
    (tourId) => {
      if (!tourId || !userId) return;
      try {
        localStorage.setItem(skippedKey(userId, tourId), '1');
      } catch {
        // ignore quota errors
      }
    },
    [userId],
  );

  const clearTourProgress = useCallback(
    (tourId) => {
      if (!userId) return;
      try {
        if (tourId) {
          localStorage.removeItem(storageKey(userId, tourId));
          localStorage.removeItem(skippedKey(userId, tourId));
        } else {
          Object.keys(localStorage)
            .filter((k) => k.startsWith(`osabea_portal_tour_${TOUR_VERSION}_`))
            .forEach((k) => localStorage.removeItem(k));
        }
      } catch {
        // ignore
      }
    },
    [userId],
  );

  const isTourCompleted = useCallback(
    (tourId) => {
      if (!userId) return true;
      try {
        return localStorage.getItem(storageKey(userId, tourId)) === 'completed';
      } catch {
        return false;
      }
    },
    [userId],
  );

  const shouldOfferAutoStart = useCallback(
    (tourId) => {
      if (!userId) return false;
      try {
        if (localStorage.getItem(storageKey(userId, tourId)) === 'completed') return false;
        if (localStorage.getItem(skippedKey(userId, tourId)) === '1') return false;
        return true;
      } catch {
        return false;
      }
    },
    [userId],
  );

  const stopTour = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(0);
    setTargetReady(false);
    document.querySelectorAll('[data-tour-active]').forEach((el) => {
      el.removeAttribute('data-tour-active');
    });
  }, []);

  const startTour = useCallback(
    (tourId, { fromStep = 0, force = false } = {}) => {
      if (!user) return;
      if (!force && isTourCompleted(tourId)) return;
      setActiveTourId(tourId);
      setStepIndex(fromStep);
      setTargetReady(false);
    },
    [user, isTourCompleted],
  );

  const goNext = useCallback(() => {
    if (!activeTourId) return;
    if (stepIndex >= steps.length - 1) {
      markComplete(activeTourId);
      stopTour();
      return;
    }
    setStepIndex((i) => i + 1);
    setTargetReady(false);
  }, [activeTourId, stepIndex, steps.length, markComplete, stopTour]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
    setTargetReady(false);
  }, []);

  const skipTour = useCallback(() => {
    if (activeTourId) markSkipped(activeTourId);
    stopTour();
  }, [activeTourId, markSkipped, stopTour]);

  // Navigate when step declares a route
  useEffect(() => {
    if (!isActive || !step?.route) return;
    if (location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [isActive, step, location.pathname, navigate]);

  // Wait for target element after navigation / render
  useEffect(() => {
    if (!isActive || !step) {
      setTargetReady(false);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        if (!step.disableSpotlight) {
          el.setAttribute('data-tour-active', 'true');
          el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
        setTargetReady(true);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tryFind, 150);
      } else {
        setTargetReady(true);
      }
    };

    document.querySelectorAll('[data-tour-active]').forEach((node) => {
      if (node.getAttribute('data-tour-active')) node.removeAttribute('data-tour-active');
    });

    const delay = step.route && location.pathname !== step.route ? 400 : 80;
    const timer = window.setTimeout(tryFind, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.querySelectorAll('[data-tour-active]').forEach((node) => {
        node.removeAttribute('data-tour-active');
      });
    };
  }, [isActive, step, stepIndex, location.pathname]);

  // Auto-start overview once per user (non-auditor: full tour; auditor: short tour)
  useEffect(() => {
    if (!user || activeTourId) return;
    const timer = window.setTimeout(() => {
      if (shouldOfferAutoStart(TOUR_IDS.overview)) {
        startTour(TOUR_IDS.overview);
      }
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [user, activeTourId, shouldOfferAutoStart, startTour]);

  const value = useMemo(
    () => ({
      isActive,
      activeTourId,
      step,
      steps,
      stepIndex,
      stepCount: steps.length,
      targetReady,
      startTour,
      stopTour,
      goNext,
      goBack,
      skipTour,
      markComplete,
      isTourCompleted,
      clearTourProgress,
      TOUR_IDS,
    }),
    [
      isActive,
      activeTourId,
      step,
      steps,
      stepIndex,
      steps.length,
      targetReady,
      startTour,
      stopTour,
      goNext,
      goBack,
      skipTour,
      markComplete,
      isTourCompleted,
      clearTourProgress,
    ],
  );

  return (
    <PortalTourContext.Provider value={value}>
      {children}
    </PortalTourContext.Provider>
  );
}

export function usePortalTour() {
  const ctx = useContext(PortalTourContext);
  if (!ctx) {
    throw new Error('usePortalTour must be used within PortalTourProvider');
  }
  return ctx;
}